import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { ColorPicker } from '@/components/ui/ColorPicker';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { useCreateCalendar, useUpdateCalendar } from '@/hooks/useCalendar';
import { Calendar } from '@/types/calendar';
import { devLog } from '@/utils/logger';
import { formatApiError } from '@/services/api';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';

/**
 * CalendarManagementModal Component
 *
 * Modal for creating and editing calendars
 */

interface CalendarManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  editCalendar?: Calendar | null;
}

export const CalendarManagementModal = ({
  isOpen,
  onClose,
  editCalendar,
}: CalendarManagementModalProps) => {
  const { t } = useTranslation(['calendar', 'common']);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#3B82F6');
  const [isDefault, setIsDefault] = useState(false);
  const [errors, setErrors] = useState<{ name?: string }>({});

  const createMutation = useCreateCalendar();
  const updateMutation = useUpdateCalendar();

  // Reset form when modal opens/closes or editCalendar changes
  useEffect(() => {
    if (isOpen) {
      if (editCalendar) {
        setName(editCalendar.name);
        setColor(editCalendar.color);
        setIsDefault(editCalendar.isDefault);
      } else {
        setName('');
        setColor('#3B82F6');
        setIsDefault(false);
      }
      setErrors({});
    }
  }, [isOpen, editCalendar]);

  // Unsaved-work guard: dirty while open and the form diverges from the
  // loaded calendar (edit) or has any content (create).
  const isDirty = editCalendar
    ? name.trim() !== editCalendar.name ||
      color !== editCalendar.color ||
      isDefault !== editCalendar.isDefault
    : name.trim().length > 0;
  useUnsavedGuard(isOpen && isDirty, 'calendar-management-modal');

  const validate = (): boolean => {
    const newErrors: { name?: string } = {};

    if (!name.trim()) {
      newErrors.name = t('calendar_name_required_error');
    } else if (name.length > 250) {
      newErrors.name = t('calendar_name_too_long');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      if (editCalendar) {
        await updateMutation.mutateAsync({
          id: editCalendar.id,
          data: { name: name.trim(), color, isDefault },
        });
      } else {
        await createMutation.mutateAsync({
          name: name.trim(),
          color,
          isDefault,
        });
      }
      onClose();
    } catch (error) {
      devLog.error('Failed to save calendar:', error);
      // Error is handled by React Query
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const error = createMutation.error || updateMutation.error;

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      title={editCalendar ? t('edit_calendar') : t('create_calendar')}
      size="sm"
      showCloseButton={false}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Calendar Name */}
        <div>
          <label htmlFor="calendar-name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('calendar_name_label')}
          </label>
          <Input
            id="calendar-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('calendar_name_placeholder')}
            maxLength={250}
            disabled={isLoading}
            error={errors.name}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500">{t('char_count_limit', { count: name.length, max: 250 })}</p>
        </div>

        {/* Color */}
        <ColorPicker
          label={t('calendar_color')}
          value={color}
          onChange={setColor}
          disabled={isLoading}
        />

        {/* Default Calendar */}
        <Checkbox
          id="is-default"
          label={t('set_as_default')}
          checked={isDefault}
          onCheckedChange={setIsDefault}
          disabled={isLoading}
        />
        <p className="text-xs text-gray-500 -mt-4 ml-7">
          {t('default_calendar_hint')}
        </p>

        {/* Error Message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">
              {formatApiError(error).message || t('failed_save_calendar')}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end gap-1.5 sm:gap-2 pt-4 border-t border-gray-200">
          <CancelButton onClick={onClose} disabled={isLoading} />
          <Button type="submit" variant="primary" loading={isLoading}>
            {editCalendar ? t('update_calendar') : t('create_calendar')}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
